import { useEffect, useState } from "react"
import { useAnimationControls, useMotionValue } from "framer-motion"
import styled from "@emotion/styled"
import hash from "@emotion/hash"

import { useConsole } from "contexts/Console"

import getMediaQuery from "css/breakpoints"

import { useValue } from "./context"
import { Term, TermButton, TermMarker, TermLabel, Desc, DescContent } from "./DescItem"
import { DescFigureMobile } from "./DescFigure"

const cssCollapsed = `css-${hash("collapsed")}`
const cssExpanded = `css-${hash("expanded")}`

const List = styled.dl`
  grid-column: col 1 / col -1;
  grid-row: desclist;
  margin: 0;

  ${getMediaQuery("m")} {
    grid-column: col 7 / span 6;
  }
`

const Icon = styled.span`
  display: inline-block;
  justify-self: end;
  ::before,
  ::after {
    background: currentColor;
    content: "";
    display: block;
    height: 2px;
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transition: transform 0.6s;
    will-change: transform;

    html.prefers-reduced-motion & {
      transition: none;
    }
  }

  .${cssCollapsed} & {
    ::after {
      transform: rotate(90deg);
    }
  }

  position: relative;
  height: 1.75rem;
  width: 12px;

  .no-js & {
    display: none;
  }
`

function Item({ index, term_marker, term, description, meta, image, video }) {
  const { currentIndex } = useValue()

  const anim = useAnimationControls()

  const [isOpen, setIsOpen] = useState(index === 0)
  const toggle = useMotionValue(isOpen)

  const termVariants = {
    open: { color: "rgb(var(--light-black))" },
    close: { color: "rgb(118 118 118)" },
  }
  const descVariants = {
    open: { height: "auto", opacity: 1 },
    close: { height: "0", opacity: 0 },
  }

  const onClick = e => {
    toggle.set(!toggle.get())
    setIsOpen(toggle.get())
  }

  const onIndexChange = bool => anim.start(bool ? "open" : "close")
  useEffect(() => toggle.onChange(onIndexChange))
  //
  return (
    <>
      <Term variants={termVariants} initial={isOpen ? "open" : "close"} animate={anim} transition={{ type: "tween", duration: 0.3 }}>
        <TermButton
          type='button'
          id={meta.uid}
          onClick={onClick}
          className={isOpen ? cssExpanded : cssCollapsed}
          aria-expanded={isOpen}
          aria-controls={`content-${meta.uid}`}
        >
          <TermMarker dangerouslySetInnerHTML={{ __html: term_marker }} />
          <TermLabel dangerouslySetInnerHTML={{ __html: term }} />
          <Icon aria-hidden='true' />
        </TermButton>
      </Term>
      <DescFigureMobile index={index} image={image} video={video} toggle={toggle} />
      <Desc id={`content-${meta.uid}`} aria-labelledby={meta.uid}>
        <DescContent
          variants={descVariants}
          initial={isOpen ? "open" : "close"}
          animate={anim}
          transition={{ type: "tween", duration: 0.3 }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </Desc>
    </>
  )
}

export default function SpecsFeaturesMobile({ spec_items }) {
  const console = useConsole()

  return (
    <List>
      {spec_items.map(({ term_marker, term, description, image_cld: image, video_cld: video, _metadata }, i) => (
        <Item
          key={`feature-spec-${i}`}
          index={i}
          term_marker={term_marker}
          image={image}
          video={video}
          term={term}
          description={description}
          meta={_metadata}
        />
      ))}
    </List>
  )
}
